/**
 * `/ai-agents`: PostDaily's MCP server, for people who search "schedule posts
 * from ChatGPT" or "Claude social media scheduler" rather than for a scheduler.
 *
 * Every claim here must match what the server does (`docs/mcp/TOOLS.md`,
 * `apps/server/src/mcp/`) and what the `connect-ai-apps` help article says.
 * Change them together. In particular:
 *
 * - It is not plan-gated, so "every plan" is true, and the writing is done by
 *   the AI app's own model, so it uses no PostDaily AI credits.
 * - Publishing right away (and retrying a failure) needs the person's go-ahead
 *   in the chat (`confirmPublish`); scheduling into the queue does not.
 * - Only Claude's connector directory has accepted the listing. The ChatGPT
 *   and Cursor directory reviews are pending, so do not call either an
 *   official listing until they are published (`docs/mcp/directory-submissions.md`).
 */

import type { CrossLink } from "./cross-links";

/** Apps that sign in through PostDaily's consent page. */
export const MCP_SERVER_URL = "https://mcp.postdaily.app/mcp";

/** Apps and automations that send an API key instead. */
export const MCP_KEY_SERVER_URL = "https://mcp.postdaily.app/key/mcp";

/** The published Community listing (approved 2026-10-01). */
export const CLAUDE_DIRECTORY_URL =
  "https://claude.ai/directory/connectors/postdaily";

/**
 * What a person types, and what PostDaily does with it. Each one maps onto
 * shipped tools or prompts, named in the comment beside it.
 */
export const AI_AGENT_EXAMPLES: readonly { ask: string; does: string }[] = [
  {
    // create_post + validate_post
    ask: "Write a launch post for Instagram and Bluesky and add it to my queue.",
    does: "Writes a version per network, checks each against that network's rules, and puts it in the next free slot.",
  },
  {
    // plan_week prompt + get_queue
    ask: "Plan next week's posts about our new feature and fill the empty slots.",
    does: "Reads your queue, drafts a post for each free slot and schedules them.",
  },
  {
    // repurpose_content prompt + import_media
    ask: "Turn this blog post into a thread for Threads and X.",
    does: "Splits it into posts that fit each network's limit, with the image from the article.",
  },
  {
    // get_analytics + get_best_times
    ask: "What did best last month, and when should I post?",
    does: "Reads your analytics and your own best posting times, channel by channel.",
  },
  {
    // fix_failed_posts prompt + retry_post
    ask: "Why did yesterday's TikTok post fail? Retry it if you can.",
    does: "Explains the network's error and, once you say yes, retries the failed channel.",
  },
];

export const AI_AGENT_STEPS: readonly { heading: string; body: string }[] = [
  {
    heading: "Add PostDaily to your AI app",
    body: `Paste the server URL, ${MCP_SERVER_URL}, where your app adds connectors or MCP servers. Settings → AI agents in PostDaily has the steps for each app, several with a one-click install.`,
  },
  {
    heading: "Approve access in PostDaily",
    body: "The app sends you to PostDaily to sign in. You choose what it can do and which workspaces it can see. You never paste a password or a key into the app.",
  },
  {
    heading: "Ask for what you want",
    body: "List your channels, write a post, schedule it, check your queue or read your analytics, in plain language. The posts land in the same queue and calendar as the ones you make in PostDaily.",
  },
];

export const AI_AGENT_SAFEGUARDS: readonly { heading: string; body: string }[] =
  [
    {
      heading: "It asks before anything goes out right away",
      body: "Even with full access, the app has to confirm with you in the chat before it publishes a post immediately or retries a failed one. Scheduled posts wait in your queue, where you can still edit or cancel them.",
    },
    {
      heading: "You choose what it can do",
      body: "Full access can schedule and publish. Drafts only can write drafts and upload media, and nothing goes out until someone acts in PostDaily. Read only can look but not change anything.",
    },
    {
      heading: "Your rules still apply",
      body: "Plan limits, each network's rules and client approval work exactly as they do in the app. In a workspace that requires approval, a post the app schedules waits for your client.",
    },
    {
      heading: "Disconnect it at any time",
      body: "Settings → AI agents lists every connected app and API key, with its workspaces and when it was last used. Disconnecting takes effect on its next request.",
    },
  ];

export const AI_AGENT_FAQS: readonly { q: string; a: string }[] = [
  {
    q: "What is an MCP server?",
    a: "MCP (Model Context Protocol) is the standard way AI apps connect to other tools. PostDaily's MCP server gives an app such as Claude or ChatGPT a set of actions it can take in your account, like listing channels, creating a post or reading analytics, and only the ones your access level allows.",
  },
  {
    q: "Does it cost extra?",
    a: "No. It works on every PostDaily plan, Free included, within that plan's limits. The writing is done by your AI app's own model, so it does not use your PostDaily AI credits. You need an account with the AI app itself.",
  },
  {
    q: "Which AI apps work with PostDaily?",
    a: "Claude (web, desktop and mobile), ChatGPT, Claude Code, Codex, Cursor, VS Code, Gemini CLI, Devin and Windsurf, Zed, Goose and LM Studio sign in with PostDaily. Cline, JetBrains AI Assistant, n8n, Make and your own scripts use an API key. Any other app that supports remote MCP servers can connect too.",
  },
  {
    q: "Can the AI publish a post without asking me?",
    a: "Not straight away. Publishing immediately, or retrying a failed post, needs your go-ahead in the chat first. Scheduling into your queue does not, but the post waits there, where you can edit or cancel it. Give the app Drafts only access if you want every post to go through you in PostDaily.",
  },
  {
    q: "Can it post my own photos and videos?",
    a: "Yes. Chat apps such as Claude and ChatGPT give you an upload link that opens your PostDaily media library, and attach what you upload. Coding agents such as Claude Code, Codex and Cursor can upload a file from your computer directly. Any app can also add media from a public URL.",
  },
  {
    q: "What does the AI app see?",
    a: "Only the workspaces you tick when you approve it, and only what its access level allows. What it reads, such as your posts and analytics, passes through that AI app, so its own privacy terms apply to it. PostDaily never gives it your social network passwords or tokens.",
  },
];

export const AI_AGENT_LINKS: readonly CrossLink[] = [
  { help: "connect-ai-apps", label: "Connect Claude, ChatGPT and other apps" },
  { help: "write-with-ai", label: "Write posts with PostDaily's own AI" },
  { help: "when-a-post-fails", label: "What happens when a post fails" },
  { to: "/client-approvals", label: "Client approval without logins" },
  { to: "/pricing", label: "Plans and limits" },
];
